import React, { useState } from 'react';
import { useUa } from './UserAdminContext';
import * as api from './userAdminApi';
import Icon from './UaIcon';
import PageHeader from './UaHeader';
import { Modal } from './UaOverlay';
import { Field, PageSwitch } from './UaBits';
import { EMPTY_FILTERS, LV, allPages, plural, roleById, roleMembers } from './uaModel';

/** New role, or rename the chosen one. */
function RoleNameDialog({ kind, role, onClose, onDone }) {
    const { data, run } = useUa();
    const [name, setName] = useState(kind === 'rename' ? role.name : '');
    const [from, setFrom] = useState(role.id);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const n = name.trim();
        if (!n) { setError('Give the role a name.'); return; }
        if (data.roles.some((r) => r.name.toLowerCase() === n.toLowerCase() && !(kind === 'rename' && r.id === role.id))) {
            setError('There’s already a role with that name.');
            return;
        }
        setBusy(true);
        const res = kind === 'new'
            ? await run(api.createRole(from, n), <>Created <b>{n}</b>. Now choose its pages.</>)
            : await run(api.updateRole(role.id, { name: n }), <>Renamed to <b>{n}</b>.</>);
        setBusy(false);
        if (res) onDone(kind === 'new' ? res.result.id : role.id);
    };

    return (
        <Modal
            onClose={onClose} title={kind === 'new' ? 'New role' : 'Rename role'}
            text={kind === 'new' ? 'It starts with the same pages as the role you pick. Change them after.' : null}
            footer={(
                <>
                    <button type="button" className="ua-btn" onClick={onClose}>Cancel</button>
                    <button type="submit" form="ua-role-form" className="ua-btn ua-btn-primary" disabled={busy}>{kind === 'new' ? 'Create role' : 'Save'}</button>
                </>
            )}
        >
            <form id="ua-role-form" className="ua-stack" onSubmit={submit} noValidate>
                <Field id="ua-role-name" label="Name" error={error}>
                    <input id="ua-role-name" className="ua-inp" value={name} maxLength={40} autoComplete="off" onChange={(e) => { setName(e.target.value); setError(''); }} />
                </Field>
                {kind === 'new' ? (
                    <Field id="ua-role-from" label="Start from">
                        <select id="ua-role-from" className="ua-sel" value={from} onChange={(e) => setFrom(e.target.value)}>
                            {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </Field>
                ) : null}
            </form>
        </Modal>
    );
}

/** Roles: pick a role, turn its pages on or off, save once. A change applies to everyone with that role. */
export default function RolesTab() {
    const { catalog, data, run, toast, setFilters, setSel, goTab } = useUa();
    const [roleId, setRoleId] = useState(() => (data.roles.find((r) => !r.admin) || data.roles[0]).id);
    const [draft, setDraft] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [busy, setBusy] = useState(false);
    const role = roleById(data, roleId) || data.roles[0];
    const levels = draft || role.levels;
    const members = roleMembers(data, role.id);
    const changes = draft ? allPages(catalog).filter((p) => draft[p.id] !== role.levels[p.id]) : [];

    const blockedByDraft = () => {
        if (!draft) return false;
        toast('Save or discard your changes first.', { kind: 'info' });
        return true;
    };

    const pickRole = (id) => { if (!blockedByDraft()) setRoleId(id); };

    const setPage = (pageId, level) => {
        const next = { ...(draft || role.levels), [pageId]: level };
        setDraft(allPages(catalog).some((p) => next[p.id] !== role.levels[p.id]) ? next : null);
    };

    const save = async () => {
        setBusy(true);
        const res = await run(api.updateRole(role.id, { levels: draft }), <>Saved. Everyone with the <b>{role.name}</b> role gets the change.</>);
        setBusy(false);
        if (res) setDraft(null);
    };

    const showMembers = () => {
        setFilters((old) => ({ ...EMPTY_FILTERS, sort: old.sort, dir: old.dir, role: role.id }));
        setSel(new Set());
        goTab('users');
    };

    return (
        <>
            <PageHeader
                tab="roles" title="Roles" count={data.roles.length}
                sub="A role decides which pages people can open. A change here applies to everyone with that role."
                actions={<button type="button" className="ua-btn" onClick={() => { if (!blockedByDraft()) setDialog('new'); }}><Icon name="plus" />New role</button>}
            />
            <section className="ua-card">
                <div className="ua-role-bar">
                    <Field id="ua-role-pick" label="Role">
                        <select id="ua-role-pick" className="ua-sel" value={role.id} onChange={(e) => pickRole(e.target.value)}>
                            {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </Field>
                    <span className="ua-meta">
                        Used by {plural(members.length, 'user')}
                        {members.length ? <> · <button type="button" className="ua-link" onClick={showMembers}>Show them</button></> : null}
                    </span>
                    {!role.admin ? (
                        <span className="ua-end">
                            <button type="button" className="ua-btn ua-btn-sm" onClick={() => { if (!blockedByDraft()) setDialog('rename'); }}>Rename</button>
                        </span>
                    ) : null}
                </div>
                <div className="ua-role-desc">
                    {role.desc}
                    {role.admin ? ' This role always has full access and can’t be changed, so nobody can lock IT out of Bridge.' : ''}
                </div>
                <div className="ua-tw">
                    <table className="ua-t ua-t-roles">
                        <thead><tr><th>Page</th><th className="ua-lvl-cell">Access</th></tr></thead>
                        <tbody>
                            {catalog.sections.map((s) => (
                                <React.Fragment key={s.id}>
                                    <tr className="ua-grp"><td colSpan={2}>{s.name}</td></tr>
                                    {s.pages.map((pg) => {
                                        const level = role.admin ? 1 : levels[pg.id] || 0;
                                        const changed = !!draft && level !== role.levels[pg.id];
                                        return (
                                            <tr key={pg.id} className={changed ? 'ua-changed' : undefined}>
                                                <td>
                                                    {pg.name}
                                                    {pg.note ? <span className="ua-pnote">{pg.note}</span> : null}
                                                    {changed ? <span className="ua-pnote ua-pnote-was">Was {LV[role.levels[pg.id] || 0].toLowerCase()}</span> : null}
                                                </td>
                                                <td className="ua-lvl-cell">
                                                    <PageSwitch label={pg.name} on={level} locked={role.admin} onChange={(v) => setPage(pg.id, v)} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                {changes.length ? (
                    <div className="ua-savebar">
                        <span className="ua-savebar-tx">
                            <b>{plural(changes.length, 'unsaved change')} to {role.name}</b>
                            <small>Applies to {plural(members.length, 'user')}.</small>
                        </span>
                        <button type="button" className="ua-btn ua-btn-sm ua-btn-ghost" onClick={() => setDraft(null)}>Discard</button>
                        <button type="button" className="ua-btn ua-btn-sm ua-btn-primary" onClick={save} disabled={busy}>Save</button>
                    </div>
                ) : null}
            </section>

            {dialog ? (
                <RoleNameDialog
                    kind={dialog} role={role} onClose={() => setDialog(null)}
                    onDone={(id) => { setDialog(null); setRoleId(id); }}
                />
            ) : null}
        </>
    );
}
